import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  Equipment,
  UpdateEquipmentPayload,
} from 'contracts/temperature-control/equipments';
import { useCompanyOptions } from 'hooks/api/options/general';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, InputGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components/activityIndicators';

import { formSchema, type FormSchema } from './form-chema';

type Ref = {
  open: (equipment: Equipment) => void;
  close: () => void;
}

type Props = {
  onSubmit: (data: UpdateEquipmentPayload) => void;
  loading?: boolean;
}

export type UpdateEquipmentDrawerRef = Ref;

export const UpdateEquipmentDrawer = React.forwardRef<Ref, Props>(({
                                                                     loading,
                                                                     onSubmit,
                                                                   }, ref) => {

  const [open, setOpen] = React.useState(false);

  const companyOptions = useCompanyOptions({
    labelType: 'sap-code-trade-name-and-document',
    onlyWithSapCode: true,
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      containerCode: '',
      equipmentCode: '',
    },
  });

  const onOpenChange = React.useCallback((open: boolean, equipment?: Equipment) => {
    form.reset();
    if (equipment) {
      form.setValue('id', equipment.id);
      form.setValue('containerCode', equipment.containerCode);
      form.setValue('equipmentCode', equipment.equipmentCode);
      form.setValue('companyId', equipment.companyId.toString());
    }
    setOpen(open);
  }, [form]);

  const handleSubmit = React.useCallback((formData: FormSchema) => {
    onSubmit({
      id: formData.id,
      data: {
        containerCode: formData.containerCode,
        equipmentCode: formData.equipmentCode,
        companyId: formData.companyId,
      }
    });
  }, [onSubmit]);

  React.useImperativeHandle(ref, () => {
    return {
      open: (equipment) => onOpenChange(true, equipment),
      close: () => onOpenChange(false),
    };
  }, [onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className="max-w-[480px] w-full mx-auto">
          <Drawer.Header>
            <Drawer.Title className={'font-GilroyBold text-gray-600 text-[16px]'}>
              Novo equipamento
            </Drawer.Title>
          </Drawer.Header>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className={'flex flex-col gap-4 px-4 pb-10'}
            >
              <FormField
                control={form.control}
                name={'companyId'}
                render={({ field }) => (
                  <ComboboxGs
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    content={companyOptions.singleSelectOptions}
                    label={'Filial'}
                    portal={false}
                    onChange={(value: string) => {
                      const company = companyOptions.findCompany(value);
                      if (company?.sapCode) {
                        const currentValue = form.getValues('equipmentCode');
                        const [_, ...rest] = currentValue.split('-');
                        form.resetField('equipmentCode');
                        form.setValue('equipmentCode', `${company.sapCode}-${rest.join('-')}`);
                        form.setFocus('equipmentCode');
                      }
                      field.onChange(value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'equipmentCode'}
                render={({ field }) => (
                  <InputGs
                    {...field}
                    label={'Código do equipamento'}
                    onChange={field.onChange}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'containerCode'}
                render={({ field }) => (
                  <InputGs
                    {...field}
                    label={'Código do container'}
                    onChange={field.onChange}
                  />
                )}
              />

              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});